<template>
  <div id="digitalization-documents-overview">
    <title-panel
      :button-label="$t('digitalizationDocumentsOverview.continueProcess')"
      :count="pageOptions.rows"
      :disabled="!canGoToMetadataGridEditor()"
      :display-button="!!selectedDocuments.length"
      :on-button-click="handleContinueProcessButtonClick"
      :title="$t('digitalizationDocumentsOverview.documents')"
    >
    </title-panel>

    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      v-model:selected="selectedDocuments"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :loading="isLoading"
      :items="documents"
      :mode="TableMode.LAZY"
      :row-class="getRowClass"
      row-clickable
      selection
      :table-style="{ 'table-layout': 'fixed', 'word-break': 'break-all' }"
      @row-click="goToDocumentDetailView"
    >
      <template #cell(hasRendition)="{ data }">
        <font-awesome-icon v-if="data?.hasRendition" :icon="faCheck" />
        <font-awesome-icon v-else :icon="faXmark" />
      </template>
      <template #cell(referenceNumber)="{ data }">
        {{ data.referenceNumber }}
        <template v-if="data.previousReferenceNumbers?.length">
          <div v-tooltip.top="data.previousReferenceNumbers.join(', ')">
            {{ $t('digitalizationDocumentsOverview.plusOthers') }}
          </div>
        </template>
      </template>
      <template #cell(checkBotInferenceStatus)="{ data }">
        <span>{{
          $t(`checkBotInferenceStatus.${data.checkBotInferenceStatus}`)
        }}</span>
        <span
          v-if="
            checkbotQueueCount !== null &&
            (data.checkBotInferenceStatus === CheckBotInferenceStatus.WAITING ||
              data.checkBotInferenceStatus ===
                CheckBotInferenceStatus.PENDING ||
              data.checkBotInferenceStatus ===
                CheckBotInferenceStatus.PROCESSING)
          "
          :title="$t('digitalizationDocumentsOverview.queueCount')"
        >
          ({{ checkbotQueueCount }})
        </span>
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import {
    RowClass,
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { endpointsList, formatDateForDisplay } from '@/helpers';
  import {
    Role,
    DocumentStatus,
    IDigitalizationDocumentSummaryDto,
    IPageDto,
    Order,
    ProjectType,
    IDigitalizationDocumentSummaryPageOptionsDto,
    CheckBotInferenceStatus,
    ICheckbotQueueCount,
  } from 'types';
  import { METADATA_GRID_DOCUMENT_IDS_LOCAL_STORAGE_KEY } from '@/interfaces';
  import { MetadataGridEditorMode } from '@/views/MetadataGridEditor/MetadataGridEditor.types';
  import axios, { AxiosResponse } from 'axios';
  import {
    PROJECT_ID_PARAM,
    PROJECT_TYPE_PARAM,
    Route,
    routeParams,
    UPLOAD_REFERENCE_NUMBER_PARAM,
  } from '@/routes';
  import { TABLE_NAMESPACE } from './constants';
  import { faFileCad } from '@fortawesome/pro-solid-svg-icons';
  import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

  const props = defineProps<{
    digitalizationProjectId: string;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const router = useRouter();
  const { isUserRole } = useRoles();

  const documents = ref<IDigitalizationDocumentSummaryDto[]>([]);
  const selectedDocuments = ref<IDigitalizationDocumentSummaryDto[]>([]);
  const checkbotQueueCount = ref<number | null>(null);

  const { isLoading, pageOptions, filters, filterParams } = useTableWrapper<
    IDigitalizationDocumentSummaryDto,
    IDigitalizationDocumentSummaryPageOptionsDto
  >(
    TableMode.LAZY,
    'uploadDate',
    getDocuments,
    Order.DESC,
    true,
    TABLE_NAMESPACE,
  );
  const tableFields = computed(() =>
    new TableFieldsBuilder<IDigitalizationDocumentSummaryDto>({
      labelTranslationKey: 'digitalizationDocumentsOverview',
      sortable: true,
    })
      .addField('documentId', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.documentId'),
        width: '9%',
      })
      .addField('hasRendition', {
        width: '5%',
        icon: faFileCad,
        sortable: false,
      })
      .addField('title', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.title'),
        width: '18%',
      })
      .addField('referenceNumber', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.referenceNumber'),
        width: '9%',
      })
      .addField('revision', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.revision'),
        width: '7%',
      })
      .addField('uploadDate', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.uploadDate'),
        width: '11%',
        valueGetter: (item) => formatDateForDisplay(new Date(item.uploadDate)),
      })
      .addField('coworker', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.coworker'),
        width: '13%',
      })
      .addField('checkBotInferenceStatus', {
        sortable: true,
        label: t('digitalizationDocumentsOverview.checkBotInferenceStatus'),
        width: '12%',
        valueTranslationKey: 'checkBotInferenceStatus',
      })
      .addField('documentStatus', {
        sortable: false,
        label: t('digitalizationDocumentsOverview.documentStatus'),
        width: '13%',
        valueTranslationKey: 'digitalizationDocumentStatus',
      })
      .build(),
  );
  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      IDigitalizationDocumentSummaryDto,
      IDigitalizationDocumentSummaryPageOptionsDto
    >({
      translationKey: 'digitalizationDocumentsOverview',
    })
      .addInputFilter('documentId', {
        width: '100px',
        simpleLabel: true,
      })
      .addInputFilter('title', {
        width: '200px',
        simpleLabel: true,
      })
      .addInputFilter('referenceNumber', {
        width: '100px',
        simpleLabel: true,
      })
      .addInputFilter('revision', {
        width: '100px',
        simpleLabel: true,
      })
      .addDateRangeFilter('uploadDate', {
        dateFromKey: 'uploadDateFrom',
        dateToKey: 'uploadDateTo',
        simpleLabel: true,
      })
      .addInputFilter('coworker', {
        width: '120px',
        simpleLabel: true,
      })
      .addSelectFilter('checkBotInferenceStatus', {
        width: '120px',
        simpleLabel: true,
        selectOptions: Object.values(CheckBotInferenceStatus),
        translationKey: 'checkBotInferenceStatus',
      })
      .addSelectFilter('documentStatus', {
        width: '120px',
        simpleLabel: true,
        selectOptions: Object.values(DocumentStatus),
        translationKey: 'digitalizationDocumentStatus',
      })
      .build(),
  );

  async function getDocuments(): Promise<void> {
    try {
      const response: AxiosResponse<
        IPageDto<IDigitalizationDocumentSummaryDto>
      > = await axios.get(
        endpointsList.digitalizationUploads.getPaginatedDigitalizationDocumentsSummary(
          {
            digitalizationProjectId: props.digitalizationProjectId as any,
            order: pageOptions.value.sortOrder,
            page: pageOptions.value.page,
            sortBy: pageOptions.value.sortBy,
            take: pageOptions.value.perPage,
            ...filterParams.value,
          },
        ),
      );
      const pageDto = response.data;
      documents.value = pageDto.data;
      pageOptions.value.rows = pageDto.meta.itemCount;
    } catch {
      createErrorToast(
        t('digitalizationDocumentsOverview.errors.errorGettingDocuments.long'),
      );
    }
  }

  async function getCheckbotQueueCount(): Promise<void> {
    try {
      const response: AxiosResponse<ICheckbotQueueCount> = await axios.get(
        endpointsList.checkBot.getQueueCount,
      );
      checkbotQueueCount.value = response.data.count;
    } catch {
      createErrorToast(
        t('digitalizationDocumentsOverview.errors.errorGettingDocuments.long'),
      );
    }
  }

  function getRowClass(document: IDigitalizationDocumentSummaryDto): string {
    if (document.didSharePointUploadFail) {
      return RowClass.DANGER;
    } else return '';
  }

  function goToDocumentDetailView(document: IDigitalizationDocumentSummaryDto) {
    router.push({
      name: Route.DOCUMENT_DETAIL_VIEW,
      params: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW][PROJECT_TYPE_PARAM]]:
          ProjectType.DIGITALIZATION_PROJECT,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].documentOid]:
          document._id.toString(),
      },
      query: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].query[PROJECT_ID_PARAM]]:
          props.digitalizationProjectId,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].query[
          UPLOAD_REFERENCE_NUMBER_PARAM
        ]]: document.referenceNumber,
      },
    });
  }

  function canGoToMetadataGridEditor(): boolean {
    let gridEditorMode: MetadataGridEditorMode | undefined = undefined;
    try {
      gridEditorMode = getGridEditorMode();
    } catch {
      return false;
    }
    const isSameContentType =
      new Set(
        selectedDocuments.value.map((document) => document?.contentTypeName),
      ).size === 1;
    const isAllowed = !(
      !isQualityCheckModeAllowed() &&
      gridEditorMode === MetadataGridEditorMode.QUALITY_CHECK
    );
    return isSameContentType && isAllowed;
  }

  function getGridEditorMode(): MetadataGridEditorMode {
    const selectedDocumentsList = selectedDocuments.value;
    if (
      selectedDocumentsList.length &&
      (selectedDocumentsList.every(
        (doc) => doc.documentStatus === DocumentStatus.METADATA_INSPECTION,
      ) ||
        selectedDocumentsList.every(
          (doc) => doc.documentStatus === DocumentStatus.FORMAL_CONTROL,
        ))
    ) {
      return MetadataGridEditorMode.METADATA_INSPECTION;
    } else if (
      selectedDocumentsList.length &&
      selectedDocumentsList.every(
        (doc) => doc.documentStatus === DocumentStatus.QUALITY_CHECK,
      )
    ) {
      return MetadataGridEditorMode.QUALITY_CHECK;
    } else {
      throw new Error('Could not establish MetadataGridEditorMode');
    }
  }

  function handleContinueProcessButtonClick() {
    if (!canGoToMetadataGridEditor()) {
      return;
    }
    setLocalStorageDataForMetadataEditor();
    router.push({
      name: Route.METADATA_GRID_EDITOR,
      params: {
        [routeParams[Route.METADATA_GRID_EDITOR].digitalizationProjectId]:
          props.digitalizationProjectId,
        [routeParams[Route.METADATA_GRID_EDITOR].mode]: getGridEditorMode(),
      },
      query: {
        [routeParams[Route.METADATA_GRID_EDITOR].query[PROJECT_ID_PARAM]]:
          props.digitalizationProjectId,
      },
    });
  }

  function setLocalStorageDataForMetadataEditor(): void {
    const ids = selectedDocuments.value.map((document) => ({
      documentObjectId: document._id.toString(),
      uploadId: document.uploadId.toString(),
    }));
    localStorage.setItem(
      METADATA_GRID_DOCUMENT_IDS_LOCAL_STORAGE_KEY,
      JSON.stringify(ids),
    );
  }

  function isQualityCheckModeAllowed(): boolean {
    return isUserRole([Role.Admin, Role.DocuResponsible]);
  }

  onMounted(async () => {
    await getCheckbotQueueCount();
  });
</script>

<style scoped lang="scss">
  #digitalization-documents-overview {
    padding: 2rem 0;

    .table-top-panel {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
